import React, { useCallback, useContext, useEffect } from 'react'
import { getCurrentPrice, getSecurityDeposit } from 'app/React/Helpers/PriceHelper'
import { getColorImage } from 'app/React/Helpers/ColorImageHelper'
import { getSettings } from 'app/React/Helpers/StepHelper'
import * as _ from 'lodash/fp'
import { pipe } from 'ts-pipe-compose'
import PropTypes from 'prop-types'
import '@reach/tooltip/styles.css'
import './style.scss'
import { AccessoryOption } from 'app/React/Components/Form/AccessoryOption'
import { FeatureFlag } from 'utils/feature-flag'
import { ConfiguratorContext } from "app/React/ConfiguratorContainer";

export default function Option(props) {
  const {
    cartItems,
    colorSettingId,
    currentEngineTransmission,
    currentVariant,
    disabledEquipmentRequirements,
    identifier,
    order,
    setCheckedEquipmentRequirements,
    setColorImage,
    setColorName,
    setDisabledEquipmentRequirements,
    setInteriorImage,
    setOrder,
    defaultValidateSteps
  } = useContext(ConfiguratorContext)

  const option = props.option
  const inputName = 'option' + props.settingid
  const inputValue = option.id

  const _setColorImage = useCallback(
    function (image) {
      const colorName = pipe(
        getSettings(currentVariant),
        _.find((s) => s.id === colorSettingId),
        _.get('options'),
        _.values,
        _.find((o) => o.id === option.id),
        _.get('name')
      )
      setColorName(colorName)
      setColorImage(image)
    },
    [setColorImage, setColorName, colorSettingId, currentVariant, identifier, option.id]
  )

  const updateColorImage = useCallback(
    function (optionId) {
      const image = getColorImage(currentVariant, optionId)
      _setColorImage(image)
    },
    [currentVariant, _setColorImage]
  )

  const disabled = pipe(
    option.option_selectable_rules_option,
    _.castArray,
    _.filter(Boolean),
    _.all((rule) =>
      pipe(
        order.configuration,
        _.omit(['variants', 'engineTransmission']),
        Object.values,
        _.some(_.propEq('id', rule)),
        (value) => {
          return option.option_selectable_rules_is_selected === '0' ? !value : value
        },
        Boolean
      )
    ),
    _.negate(Boolean)
  )

  useEffect(() => {
    if (disabled) {
      if (order.configuration[props.settingid]?.id == option.id) {
        let newConfiguration = { ...order.configuration }
        delete newConfiguration[props.settingid]

        order.configuration = newConfiguration
        setOrder({ ...order })

        defaultValidateSteps()
      }
    }
  }, [order.configuration])

  const labelHoverMouseEnter = () => {
    props.setHoverColorOption(option)
  }
  const labelHoverMouseLeave = () => {
    props.setHoverColorOption(null)
  }

  const hidden = option.hide_on === order.paymentType || option.hide_on === 'all'
  const onClick = function () {

    if (disabled) return

    if (
      typeof order.configuration[props.settingid] === 'object' &&
      order.configuration[props.settingid] !== null &&
      order.configuration[props.settingid].id === props.option.id
    ) {
      // User clicked the already chosen option!

      if (props.required && props.required !== '0') {
        setInteriorImage(order.configuration[props.settingid])
        return
      } // This setting is required so don't de-select it
      updateValue(true)
    }
  }

  const updateValue = useCallback(
    function (shouldDelete, option = props.option) {
      if (hidden) return
      if (typeof shouldDelete !== 'boolean') shouldDelete = false

      if (typeof props.onChange === 'function' && !shouldDelete) {
        const shouldStillupdate = props.onChange(props.settingid, option)
        if (shouldStillupdate !== true) return
      }

      const newConfiguration = shouldDelete
        ? _.omit(props.settingid, order.configuration)
        : _.set(props.settingid, option, order.configuration)

      order.configuration = newConfiguration
      order.price = getCurrentPrice(order.paymentType, order, currentVariant, cartItems)
      order.security_deposit = getSecurityDeposit(order.paymentType, order, currentVariant, currentEngineTransmission, cartItems)
      setOrder({ ...order })

      if (shouldDelete) {
        props.setChecked(null)
      } else {
        props.setChecked(option.id)
      }
      if (colorSettingId === props.settingid) {
        updateColorImage(shouldDelete ? null : option.id)
      }

      setInteriorImage(shouldDelete ? null : newConfiguration[props.settingid])

      defaultValidateSteps()

      //reset extra equipments when changing variant
      order.configuration.extra_equipments = {}
      setOrder({ ...order })
      setCheckedEquipmentRequirements({})

      if (disabledEquipmentRequirements !== null) {
        setDisabledEquipmentRequirements({
          required: disabledEquipmentRequirements.required,
          excludes: {}
        })
      }
    }
  )

  const htmlId = 'option' + props.settingid + '_' + props.option.id + (props.htmlIdPostfix ? '_' + props.htmlIdPostfix : '')

  if (props.option.type === 'accessory-product') {
    if (!FeatureFlag.accessoryShop()) {
      return null
    }
    return <AccessoryOption {...props} />
  }
  if (hidden) {
    return null
  }
  // endregion

  return (
    <>
      <input
        id={htmlId}
        type='radio'
        name={inputName}
        value={inputValue}
        checked={props.checked === props.option.id}
        data-checked={props.checked === props.option.id}
        onChange={updateValue}
        onClick={onClick}
        disabled={disabled}
        required={props.required}
      />
      <label
        htmlFor={htmlId}
        className='option'
        layout={props.layout}
        data-disabled={disabled}
        onMouseEnter={() => labelHoverMouseEnter()}
        onMouseLeave={() => labelHoverMouseLeave()}
      >
        {
          option.color_hex.substr(0, 1) === '#'
            ? (<span className='color' style={{ backgroundColor: option.color_hex }}/>)
            : typeof option.image_url === 'string' && option.image_url.length >= 0
              ? (<img className='image' src={option.image_url} alt=''/>)
              : null
        }
      </label>
    </>
  )
}

Option.defaultProps = {
  onChange: null,
  hide_on: 'none',
  hidePrice: false
}
Option.propTypes = {
  layout: PropTypes.any,
  checked: PropTypes.any,
  settingid: PropTypes.any,
  htmlIdPostfix: PropTypes.any,
  setChecked: PropTypes.any,
  onChange: PropTypes.any,
  required: PropTypes.any,
  hidePrice: PropTypes.any,

  option: PropTypes.shape({
    id: PropTypes.any,
    name: PropTypes.any,
    color_hex: PropTypes.any,
    image_url: PropTypes.any,
    min_price_leasing: PropTypes.any,
    security_deposit_leasing: PropTypes.any,
    variant_index: PropTypes.any,
    price: PropTypes.any,
    min_price: PropTypes.any,
    use_leasing_price: PropTypes.any,
    option_selectable_rules_option: PropTypes.any,
    option_selectable_rules_is_selected: PropTypes.any,
    hide_on: PropTypes.any,
    price_leasing: PropTypes.any,
    title: PropTypes.any,
    description: PropTypes.any,
    description_override: PropTypes.any,
    description_title: PropTypes.any
  })
}
